import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from '@redux-requests/react';

import LoadingWrapper from 'shared/components/LoadingWrapper';
import { ConfirmationModal } from 'shared/components/ConfirmationModal';
import { Safelist, SafelistTerm } from 'shared/types/safelist';
import { REMOVE_TERM_MODAL } from 'shared/constants/modalNames';
import { openModal } from 'services/modal/actions';
import { patchSafelist } from 'services/safelists/actions';
import { getSafelistToEdit } from 'services/safelists/getters';
import {
  resetSafelistEditData,
  FETCH_SAFELIST_BY_GUID,
  PATCH_SAFELIST,
} from 'services/safelists/actions';

import { SafelistDetailView } from '../SafelistDetailView';
import { filterRepeatedTerms } from '../utils';

import { RemoveTermModal } from './RemoveTermModal';

const EDIT_SAFELIST_MODAL = 'edit-safelist-modal';

const MODAL_CONTENT_WRAPPER_CLASS_NAME = 'edit-safelist-modal__content-wrapper';

const StyledConfirmationModal = styled(ConfirmationModal)`
  & {
    .${MODAL_CONTENT_WRAPPER_CLASS_NAME} {
      display: flex;
    }
  }
`;

const StyledLoadingWrapper = styled(LoadingWrapper)`
  flex: 1;
`;

const EditSafelistModal = () => {
  const dispatch = useDispatch();
  const safelistToEdit = useSelector(getSafelistToEdit);
  const [safelist, setSafelist] = useState<Safelist>(safelistToEdit);
  const [termToRemove, setTermToRemove] = useState<SafelistTerm | null>(null);
  const { loading } = useMutation({ type: PATCH_SAFELIST });

  useEffect(() => {
    setSafelist(safelistToEdit);
  }, [safelistToEdit.name, safelistToEdit.entries?.length]);

  const updateSafelist = (key: string, value: any) => {
    const updatedSafelist = {
      ...safelist,
      [key]: value,
    };

    setSafelist(updatedSafelist);
  };

  const handleTermAdding = (terms: SafelistTerm[]) => {
    const newTerms = safelist.entries.concat(terms);
    const uniqueTerms =  filterRepeatedTerms(newTerms);

    updateSafelist('entries', uniqueTerms);
  };

  const handleTermDeletion = (term: SafelistTerm) => {
    if (term.guid) {
      // If the term has a GUID, it means it has already been created in the database,
      // so we dispatch an action to start the process of removing it from the server.
      setTermToRemove(term);
      dispatch(openModal(REMOVE_TERM_MODAL));
    } else {
      // If not, we just remove the term from the local state.
      updateSafelist(
        'entries',
        safelist.entries.filter((entry) => entry.entity !== term.entity),
      );
    }
  };

  const onClose = useCallback(() => {
    dispatch(resetSafelistEditData());
  }, []);

  const onSubmit = () => {
    dispatch(patchSafelist(safelist.guid, safelist.entries));
  };

  return (
    <>
      <StyledConfirmationModal
        name={EDIT_SAFELIST_MODAL}
        contentClassName={MODAL_CONTENT_WRAPPER_CLASS_NAME}
        title='Update Safelist Library'
        submitButtonLabel='Update Safelist Library'
        onClose={onClose}
        onSubmit={onSubmit}
        isSubmitting={loading}
      >
        <StyledLoadingWrapper actionToQuery={FETCH_SAFELIST_BY_GUID}>
          <SafelistDetailView
            isNameEditable={false}
            safelistName={safelist.name}
            terms={safelist.entries}
            onDeleteTerm={handleTermDeletion}
            onAddTerms={handleTermAdding}
          />
        </StyledLoadingWrapper>
      </StyledConfirmationModal>
      {termToRemove && (
        <RemoveTermModal
          safelistGuid={safelist.guid}
          termToRemove={termToRemove}
        />
      )}
    </>
  );
};

export {
  EditSafelistModal,
  EDIT_SAFELIST_MODAL,
};
